import { isEmpty } from "lodash";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useSelector } from "react-redux";

import config from "../../../config";
import useClient from "../../../hooks/useClient";
import { getMarketTranslations } from "../../../redux/modules/translations/selector";
import { PROMISE_SETTLED_STATUS } from "../constants";
import {
  fetchFinancialSituation,
  fetchMediaMonitorPage,
  fetchPriceAlerts,
  fetchRecommendations,
  fetchStockPageData,
  fetchStockPayload,
  fetchStockTags,
  resolveMarketId
} from "../fetchStockPageApi";
import {
  STOCK_PAGE_ACTION,
  createInitialStockPageState,
  stockPageReducer
} from "../state/stockPageReducer";

function entryKey(market: string, ticker: string): string {
  return `${market}:${ticker}`;
}

type UseStockPageDataArgs = {
  market: string;
  ticker: string;
  active: boolean;
};

/** Presence-only probe so the stock page can gate the Media Monitor section. */
const MEDIA_MONITOR_PRESENCE_COUNT = 1;

export function useStockPageData({
  market,
  ticker,
  active
}: UseStockPageDataArgs) {
  const client = useClient();
  const marketTranslations = useSelector(getMarketTranslations) as Record<
    string,
    { pl?: string; en?: string }
  > | null;

  const key = entryKey(market, ticker);
  const keyRef = useRef(key);
  const loadedKeyRef = useRef<string | null>(null);
  const secondaryLoadedForStockIdRef = useRef<number | null>(null);
  const financialSituationLoadedForStockIdRef = useRef<number | null>(null);
  const financialSituationInFlightRef = useRef(false);

  const [state, dispatch] = useReducer(
    stockPageReducer,
    { key, active },
    ({ key: initialKey, active: initialActive }) =>
      createInitialStockPageState(initialKey, initialActive)
  );

  // Reset slots only when market/ticker change (keep cache on blur/refocus).
  if (state.appliedKey !== key) {
    keyRef.current = key;
    loadedKeyRef.current = null;
    secondaryLoadedForStockIdRef.current = null;
    financialSituationLoadedForStockIdRef.current = null;
    financialSituationInFlightRef.current = false;
    dispatch({
      type: STOCK_PAGE_ACTION.RESET_ENTRY,
      key,
      active
    });
  }

  const isCurrent = useCallback((expectedKey: string) => {
    return keyRef.current === expectedKey;
  }, []);

  const stockId = state.stock.data?.stock?.id;

  const reloadPriceAlerts = useCallback(async () => {
    if (!stockId || !active) {
      return;
    }

    const expectedKey = keyRef.current;
    try {
      const alerts = await fetchPriceAlerts(client, stockId);
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.PRICE_ALERTS_SUCCEEDED,
        alerts
      });
    } catch (error) {
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.PRICE_ALERTS_FAILED,
        error
      });
    }
  }, [active, client, isCurrent, stockId]);

  const reloadTags = useCallback(async () => {
    if (!stockId || !active) {
      return;
    }

    const expectedKey = keyRef.current;
    try {
      const tags = await fetchStockTags(client, stockId);
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.TAGS_SUCCEEDED,
        tags
      });
    } catch (error) {
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.TAGS_FAILED,
        error
      });
    }
  }, [active, client, isCurrent, stockId]);

  const loadMoreRecommendations = useCallback(async () => {
    if (!stockId || !active) {
      return;
    }

    const expectedKey = keyRef.current;
    const start = state.recommendations.data?.length || 0;

    dispatch({ type: STOCK_PAGE_ACTION.RECOMMENDATIONS_LOADING });

    try {
      const payload = await fetchRecommendations(
        client,
        stockId,
        start,
        config.recommendationsToFetch
      );
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.RECOMMENDATIONS_APPENDED,
        recommendations: payload.recommendations,
        hasMore: payload.more_recommendations
      });
    } catch (error) {
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.RECOMMENDATIONS_FAILED,
        error
      });
    }
  }, [active, client, isCurrent, state.recommendations.data?.length, stockId]);

  const loadFinancialSituation = useCallback(async () => {
    if (!stockId || !active) {
      return;
    }

    if (
      financialSituationLoadedForStockIdRef.current === stockId ||
      financialSituationInFlightRef.current
    ) {
      return;
    }

    const expectedKey = keyRef.current;
    financialSituationInFlightRef.current = true;
    dispatch({ type: STOCK_PAGE_ACTION.FINANCIAL_SITUATION_LOADING });

    try {
      const data = await fetchFinancialSituation(client, stockId);
      if (!isCurrent(expectedKey)) {
        return;
      }
      financialSituationLoadedForStockIdRef.current = stockId;
      dispatch({
        type: STOCK_PAGE_ACTION.FINANCIAL_SITUATION_SUCCEEDED,
        data
      });
    } catch (error) {
      if (!isCurrent(expectedKey)) {
        return;
      }
      dispatch({
        type: STOCK_PAGE_ACTION.FINANCIAL_SITUATION_FAILED,
        error
      });
    } finally {
      financialSituationInFlightRef.current = false;
    }
  }, [active, client, isCurrent, stockId]);

  // Primary fetch: singleStock + stock (wait for market translations when empty).
  // Keep per-screen cache on blur/refocus — only refetch when market/ticker change.
  useEffect(() => {
    if (!active || !market || !ticker) {
      return undefined;
    }

    if (isEmpty(marketTranslations)) {
      return undefined;
    }

    if (loadedKeyRef.current === key) {
      return undefined;
    }

    const expectedKey = key;
    let cancelled = false;
    const resolvedMarket = resolveMarketId(marketTranslations, market);

    (async () => {
      dispatch({ type: STOCK_PAGE_ACTION.PRIMARY_LOADING });

      const [pageResult, stockResult] = await Promise.allSettled([
        fetchStockPageData(client, resolvedMarket, ticker),
        fetchStockPayload(client, resolvedMarket, ticker)
      ]);

      if (cancelled || !isCurrent(expectedKey)) {
        return;
      }

      if (stockResult.status === PROMISE_SETTLED_STATUS.FULFILLED) {
        loadedKeyRef.current = expectedKey;
      }

      dispatch({
        type: STOCK_PAGE_ACTION.PRIMARY_SETTLED,
        pageResult,
        stockResult
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [active, client, isCurrent, key, market, marketTranslations, ticker]);

  // Secondary fetch once numeric stockId is known
  useEffect(() => {
    if (!active || state.stock.loading || !stockId || !market || !ticker) {
      return undefined;
    }

    if (isEmpty(marketTranslations)) {
      return undefined;
    }

    if (secondaryLoadedForStockIdRef.current === stockId) {
      return undefined;
    }

    const expectedKey = key;
    let cancelled = false;
    const resolvedMarket = resolveMarketId(marketTranslations, market);

    dispatch({ type: STOCK_PAGE_ACTION.SECONDARY_LOADING });

    (async () => {
      const [
        mediaResult,
        alertsResult,
        tagsResult,
        recommendationsResult
      ] = await Promise.allSettled([
        fetchMediaMonitorPage(
          client,
          resolvedMarket,
          ticker,
          0,
          MEDIA_MONITOR_PRESENCE_COUNT
        ),
        fetchPriceAlerts(client, stockId),
        fetchStockTags(client, stockId),
        fetchRecommendations(client, stockId, 0)
      ]);

      if (cancelled || !isCurrent(expectedKey)) {
        return;
      }

      secondaryLoadedForStockIdRef.current = stockId;
      dispatch({
        type: STOCK_PAGE_ACTION.SECONDARY_SETTLED,
        mediaResult,
        alertsResult,
        tagsResult,
        recommendationsResult
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [
    active,
    client,
    isCurrent,
    key,
    market,
    marketTranslations,
    stockId,
    state.stock.loading,
    ticker
  ]);

  return {
    pageData: state.pageData,
    stock: state.stock,
    financialSituation: state.financialSituation,
    hasMediaMonitor: state.hasMediaMonitor,
    priceAlerts: state.priceAlerts,
    stockTags: state.stockTags,
    recommendations: state.recommendations,
    hasMoreRecommendations: state.hasMoreRecommendations,
    moreRecommendationsLoading: state.moreRecommendationsLoading,
    loadMoreRecommendations,
    loadFinancialSituation,
    reloadPriceAlerts,
    reloadTags
  };
}
